import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import api from "../lib/api";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";

const HERO_IMG = "/images/hero-model-rebel.png";
const HERO_IMG_2 = "/images/manifesto-model-stoner.png";

const CHAPTERS = [
  {
    num: "01",
    title: "Built Heavy",
    body: "500GSM organic cotton. Ripstop everything. Our fabric weighs more than your excuses. Every stitch is a small act of defiance against fast fashion.",
  },
  {
    num: "02",
    title: "Worn Loud",
    body: "Earth tones pulled from city concrete and desert dusk. Espresso blacks, clay flames, oat neutrals — a palette that warms the room before you say a word.",
  },
  {
    num: "03",
    title: "Owned By You",
    body: "Limited runs. No restocks. When a drop is gone, it's gone. Popular Rebelz belongs to the ones who move first.",
  },
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(224,90,54,0.14),transparent_55%)]" />
        <motion.div style={{ y: imgY }} className="absolute right-0 top-0 bottom-0 w-full md:w-[58%]">
          <div className="relative h-full w-full [clip-path:polygon(12%_0,100%_0,100%_100%,0_100%)] max-md:[clip-path:none]">
            <img src={HERO_IMG} alt="Popular Rebelz hero model" className="h-full w-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C0A09] via-[#0C0A09]/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-transparent to-transparent" />
          </div>
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full pt-10">
          <MaskedLine delay={0.15}>
            <span className="text-xs font-mono uppercase tracking-[0.35em] text-[#E05A36]" data-testid="hero-overline">
              Est. 2026 — Streetwear for the bold
            </span>
          </MaskedLine>
          <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[9.5rem] mt-6" data-testid="hero-heading">
            <MaskedLine delay={0.3}>Popular</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-stroke">Rebelz</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.65} className="max-w-md">
            <p className="text-base sm:text-lg font-medium text-amber-100/70 leading-relaxed mt-6" data-testid="hero-subtitle">
              Heavyweight essentials for the rule-breakers. Burn the dress code. Wear the defiance.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#F06B47] hover:shadow-[0_0_25px_rgba(224,90,54,0.4)] active:scale-[0.98] transition-all animate-pulse-glow"
              data-testid="hero-shop-cta"
            >
              Shop the drop <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#manifesto"
              className="inline-flex items-center gap-2 border border-[#2C2623] hover:border-[#E05A36] hover:text-[#E05A36] px-8 py-4 font-bold uppercase tracking-wider transition-all"
              data-testid="hero-manifesto-cta"
            >
              The manifesto
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex gap-10 mt-14 font-mono text-xs uppercase tracking-[0.2em] text-[#7A7067]"
            data-testid="hero-stats"
          >
            <div><span className="block text-2xl text-[#F5F0EB] font-display font-bold">06</span>Pieces live</div>
            <div><span className="block text-2xl text-[#F5F0EB] font-display font-bold">500</span>GSM fabric</div>
            <div><span className="block text-2xl text-[#F5F0EB] font-display font-bold">00</span>Restocks</div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#7A7067]"
        >
          <ArrowDown className="w-5 h-5 animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={["Wear the rebellion", "Limited runs only", "Heavyweight or nothing", "No permission needed"]} />

      {/* FEATURED DROP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="featured-section">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36] mb-3">01 — The Drop</p>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight uppercase leading-[1.05]">
              Current drop —<br />Heat for the streets
            </h2>
            <Link to="/shop" className="text-xs font-mono uppercase tracking-[0.2em] text-[#B8AEA5] hover:text-[#E05A36] transition-colors inline-flex items-center gap-2" data-testid="featured-view-all-link">
              View all pieces <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {products.slice(0, 5).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={i === 0 ? "sm:col-span-2 sm:row-span-1" : ""}>
              <ProductCard product={p} large={i === 0} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="border-t border-[#2C2623] bg-[#11100914]" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36] mb-3">02 — The Manifesto</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight uppercase leading-[1.05] mb-8">
                Three rules.<br />Zero apologies.
              </h2>
            </Reveal>
            <div className="relative overflow-hidden [clip-path:polygon(0_0,100%_4%,96%_100%,0_96%)]">
              <img src={HERO_IMG_2} alt="Rebel manifesto" loading="lazy" className="w-full aspect-[4/5] object-cover" />
            </div>
          </div>
          <div className="flex flex-col justify-center divide-y divide-[#2C2623]">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12} className="py-10 first:pt-0">
                <div className="flex gap-6">
                  <span className="font-display font-extrabold text-4xl text-[#E05A36]/40 leading-none">{c.num}</span>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight uppercase mb-3">{c.title}</h3>
                    <p className="text-sm sm:text-base leading-relaxed text-[#B8AEA5]">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="newsletter-cta-section">
        <Reveal>
          <div className="relative overflow-hidden bg-[#E05A36] text-[#0C0A09] px-6 sm:px-14 py-16 [clip-path:polygon(0_0,100%_0,100%_88%,97%_100%,0_100%)]">
            <div className="absolute -right-10 -top-16 font-display font-extrabold uppercase text-[11rem] leading-none opacity-10 select-none pointer-events-none hidden lg:block">
              20%
            </div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] mb-4 font-bold">03 — Insider list</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight uppercase leading-[1.05] max-w-xl">
              Get 20% off your first order. Codes before anyone else.
            </h2>
            <p className="mt-4 text-sm sm:text-base font-medium max-w-md opacity-80">
              Drop your email in the footer below and your code lands instantly.
            </p>
            <a
              href="#newsletter"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('[data-testid="footer-newsletter-email-input"]')?.focus();
                window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 mt-8 bg-[#0C0A09] text-[#F5F0EB] font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#1E1A17] active:scale-[0.98] transition-all"
              data-testid="newsletter-cta-button"
            >
              Claim my code <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
